import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Route, Router } from '@angular/router';
import { Release } from '../../models/release';

@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  buildBreadcrumbs(): { label: string; url: string }[] {
    const urlSegments = this.router.url.split('/').filter(Boolean);
    const breadcrumbs: { label: string; url: string }[] = [];
    let currentPath = '';

    for (let i = 0; i < urlSegments.length; i++) {
      const segment = urlSegments[i];
      currentPath += '/' + segment;

      const matchingRoute = this.findMatchingRoute(urlSegments.slice(0, i + 1));
      if (!matchingRoute) continue;

      let label = matchingRoute.data?.['breadcrumb'];

      // For function-based breadcrumbs, we need to get the right route data
      if (typeof label === 'function') {
        try {
          // Get appropriate route snapshot and data based on the route
          let routeSnapshot: ActivatedRouteSnapshot | null = null;
          let routeData: any = {};

          if (currentPath.includes('/releases/') && currentPath.includes('/test-case/')) {
            // For test case routes, we need both release and test case data
            routeSnapshot = this.getRouteSnapshotByPath(currentPath);
            if (routeSnapshot) {
              // The test-case route resolves releaseData via the ReleaseResolver,
              // so the resolved release is already present on the snapshot data.
              routeData = routeSnapshot.data;
              routeData.releaseData = this.getResolvedReleaseData(routeSnapshot);
            }
          } else if (currentPath.includes('/releases/')) {
            // For release routes
            routeSnapshot = this.getRouteSnapshotByPath(currentPath);
            if (routeSnapshot) {
              routeData = routeSnapshot.data;
            }
          }

          if (routeSnapshot) {
            label = label(routeSnapshot.params, routeData);
          } else {
            label = '...';
          }
        } catch {
          label = '...';
        }
      }

      if (label) {
        breadcrumbs.push({ label, url: currentPath });
      }
    }

    return breadcrumbs;
  }

  private getRouteSnapshotByPath(path: string): ActivatedRouteSnapshot | null {
    // Navigate through the route tree to find the snapshot for the given path
    let current = this.route.snapshot;
    while (current.firstChild) {
      current = current.firstChild;
    }
    return current;
  }

  private getResolvedReleaseData(snapshot: ActivatedRouteSnapshot): Release | null {
    // Walk up the route tree to find the resolved release data. The ReleaseResolver
    // populates `releaseData` on the route that owns the `:id` segment, which may be
    // an ancestor of the deepest (test-case) snapshot.
    let current: ActivatedRouteSnapshot | null = snapshot;
    while (current) {
      const releaseData = current.data?.['releaseData'] as Release | null | undefined;
      if (releaseData) {
        return releaseData;
      }
      current = current.parent;
    }
    return null;
  }

  private findMatchingRoute(segments: string[]): Route | undefined {
    return this.router.config.find((route) => {
      const routeParts = route.path?.split('/');
      if (!routeParts || routeParts.length !== segments.length) return false;

      return routeParts.every((part, idx) => part.startsWith(':') || part === segments[idx]);
    });
  }
}
